import { useEffect, useMemo, useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import {
  catalogVolumeToBodymap,
  normalizeVolumeByMuscle,
  volumeBySlugToHeatmap,
  type Exercise,
  type SessionResult,
  type WorkoutEnergyReadout,
} from "@heraion/shared";
import {
  BodyMap,
  Button,
  CaloriesEffortBlock,
  SessionSummaryView,
  resolveTheme,
  useExerciseNameBySlug,
  type PostSessionSummaryHandle,
  type SummaryNote,
  type WeightUnit,
} from "@heraion/ui";

import { exerciseCatalog } from "./exercisesCache";
import { useThemePalette } from "../theme";
import { useWebShareCard } from "./useWebShareCard";

// web-rnw's LoggerHostDeps.PostSessionSummaryCard (RW-18, share capture lifted
// 2026-08-18) — a THIN mount of the shared <SessionSummaryView> +
// <ShareCardRenderer>, mirroring apps/mobile/src/logger/PostSessionSummaryCard
// .tsx's own header comment: mobile and web now share the SAME capture
// pipeline (react-native-view-shot ships a real web backend, verified
// cross-platform — see packages/ui/src/Logger/ShareCardRenderer.tsx's header
// comment). Only the Share step differs: mobile hands the captured PNG to the
// native share sheet, web hands it to shareCardCaptureWeb.ts (Web Share API
// with files, falling back to a browser download when unsupported).
type Props = {
  result: SessionResult;
  energy?: WorkoutEnergyReadout | null;
  locale: string;
  unit: WeightUnit;
  hideShareButton?: boolean;
  /** The session's timeline notes, from the draft just saved (#24). */
  notes?: readonly SummaryNote[];
};

export const PostSessionSummaryCard = forwardRef<PostSessionSummaryHandle, Props>(
  function PostSessionSummaryCard(
    { result, energy = null, locale, unit, hideShareButton = false, notes },
    handleRef,
  ) {
    const { t } = useTranslation();
    const { dark, family } = useThemePalette();
    const theme = resolveTheme(dark, family);
    const [catalog, setCatalog] = useState<ReadonlyArray<Exercise> | null>(null);

    // Hydrate the catalog so display names resolve via FK against the live
    // source-of-truth, same as mobile — exerciseResults[].exerciseName is
    // only the deleted-row fallback.
    useEffect(() => {
      let cancelled = false;
      void (async () => {
        try {
          const exs = await exerciseCatalog.fetchAndPersist();
          if (!cancelled) setCatalog(exs);
        } catch {
          // Best-effort; falls back to the snapshot name.
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);
    const getName = useExerciseNameBySlug(catalog);

    const bodyMapValue = useMemo(
      () =>
        volumeBySlugToHeatmap(
          catalogVolumeToBodymap(normalizeVolumeByMuscle(result.volumeByMuscle)),
          theme.heatmapRamp,
        ),
      [result.volumeByMuscle, theme.heatmapRamp],
    );

    const { sharing, onShare, capture } = useWebShareCard(handleRef, {
      result,
      energy,
      locale,
      unit,
      resolveName: getName,
    });

    return (
      <>
        <SessionSummaryView
          theme={theme}
          result={result}
          notes={notes}
          locale={locale}
          unit={unit}
          resolveName={getName}
          bodyMapSlot={
            Object.keys(bodyMapValue).length > 0 ? (
              <BodyMap
                mode="display"
                view="front"
                value={bodyMapValue}
                theme={theme}
                isDark={dark}
                layout="dual"
              />
            ) : undefined
          }
          energySlot={
            // LG-46: a saved/share artifact, never the live footer — the reused
            // live bundle may still carry projected=true (the draft isn't reset
            // until Done), but nothing is "about to do" here.
            energy ? (
              <CaloriesEffortBlock theme={theme} {...energy} projected={false} />
            ) : undefined
          }
          shareSlot={
            hideShareButton ? undefined : (
              <Button
                theme={theme}
                label={t("mobile.postSession.shareCta", {
                  defaultValue: "Share session card",
                })}
                onPress={() => void onShare()}
                loading={sharing}
                style={{ marginTop: theme.spacing.s2 }}
              />
            )
          }
        />

        {capture}
      </>
    );
  },
);
